import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, CogIcon } from '@heroicons/react/24/outline';
import { useSettings } from '../../../../contexts/SettingsContext';
import type { SystemSetting } from '../../../../types/settings';

const schema = z.object({
  key: z.string().min(1, 'Key is required'),
  value: z.string().min(1, 'Value is required'),
  category: z.string().min(1, 'Category is required'),
  description: z.string().optional(),
  is_public: z.boolean().default(false),
});

type FormData = z.infer<typeof schema>;

type View = 'list' | 'form';

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') return JSON.stringify(val, null, 2);
  return String(val);
}

export default function MobileSettingsSystem() {
  const { state, addSystemSetting, updateSystemSetting, deleteSystemSetting } = useSettings();
  const [view, setView] = useState<View>('list');
  const [editingSetting, setEditingSetting] = useState<SystemSetting | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { key: '', value: '', category: '', description: '', is_public: false },
  });

  const openAdd = () => {
    setEditingSetting(null);
    reset({ key: '', value: '', category: '', description: '', is_public: false });
    setView('form');
  };

  const openEdit = (s: SystemSetting) => {
    setEditingSetting(s);
    reset({
      key: s.key,
      value: typeof s.value === 'object' ? JSON.stringify(s.value, null, 2) : String(s.value ?? ''),
      category: s.category,
      description: s.description ?? '',
      is_public: s.is_public,
    });
    setView('form');
  };

  const onSubmit = async (data: FormData) => {
    let parsedValue: unknown = data.value;
    try {
      if (data.value.trim().startsWith('{') || data.value.trim().startsWith('[')) {
        parsedValue = JSON.parse(data.value);
      } else if (data.value === 'true') parsedValue = true;
      else if (data.value === 'false') parsedValue = false;
    } catch {}

    if (editingSetting) {
      await updateSystemSetting({ ...editingSetting, key: data.key, value: parsedValue, category: data.category, description: data.description, is_public: data.is_public });
    } else {
      await addSystemSetting({ key: data.key, value: parsedValue, category: data.category, description: data.description, is_public: data.is_public });
    }
    setView('list');
    reset();
  };

  const handleDelete = async (id: string, key: string) => {
    if (!window.confirm(`Delete setting "${key}"?`)) return;
    await deleteSystemSetting(id);
  };

  if (view === 'form') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-golf-gray text-base">{editingSetting ? 'Edit Setting' : 'New Setting'}</h3>
          <button onClick={() => setView('list')} className="p-1 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Key *</label>
            <input
              type="text"
              {...register('key')}
              placeholder="e.g. site_title"
              className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange font-mono"
              readOnly={!!editingSetting}
            />
            {errors.key && <p className="text-xs text-red-500 mt-1">{errors.key.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category *</label>
            <input type="text" {...register('category')} placeholder="e.g. general" className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
            {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Value * <span className="text-gray-400 font-normal">(JSON or plain text)</span></label>
            <textarea
              {...register('value')}
              rows={4}
              placeholder='e.g. "Ibom Open" or {"key": "value"}'
              className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange font-mono"
            />
            {errors.value && <p className="text-xs text-red-500 mt-1">{errors.value.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <input type="text" {...register('description')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" {...register('is_public')} className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange" />
            Public setting
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setView('list')} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="flex-1 py-2.5 rounded-xl bg-golf-orange text-white text-sm font-medium disabled:opacity-50">
              {isSubmitting ? 'Saving...' : (editingSetting ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const grouped = state.systemSettings.reduce((acc, s) => {
    if (!acc[s.category]) acc[s.category] = [];
    acc[s.category].push(s);
    return acc;
  }, {} as Record<string, SystemSetting[]>);

  return (
    <div className="space-y-4">
      <button
        onClick={openAdd}
        className="w-full flex items-center justify-center gap-2 py-2.5 bg-golf-orange text-white rounded-xl text-sm font-medium"
      >
        <PlusIcon className="w-4 h-4" />
        Add Setting
      </button>

      {state.systemSettings.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
          <CogIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No system settings configured</p>
        </div>
      ) : (
        Object.entries(grouped).map(([category, settings]) => (
          <div key={category} className="space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1 capitalize">{category}</p>
            {settings.map(s => {
              const isExpanded = expandedId === s.id;
              const valueStr = formatValue(s.value);
              const isLong = valueStr.length > 60;

              return (
                <div key={s.id} className="bg-white rounded-2xl border border-gray-100 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xs font-mono font-semibold text-golf-gray">{s.key}</p>
                        {s.is_public && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">Public</span>}
                      </div>
                      {s.description && <p className="text-xs text-gray-400 mt-0.5">{s.description}</p>}
                    </div>
                  </div>

                  <div className="mt-2">
                    <div className={`bg-gray-50 rounded-lg p-2 font-mono text-xs text-gray-600 ${!isExpanded && isLong ? 'line-clamp-2' : 'whitespace-pre-wrap'}`}>
                      {valueStr}
                    </div>
                    {isLong && (
                      <button onClick={() => setExpandedId(isExpanded ? null : s.id)} className="text-xs text-golf-orange mt-1">
                        {isExpanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>

                  <div className="flex gap-2 mt-3">
                    <button onClick={() => openEdit(s)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-golf-orange bg-golf-orange/10 rounded-lg">
                      <PencilIcon className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={() => handleDelete(s.id, s.key)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-red-500 bg-red-50 rounded-lg">
                      <TrashIcon className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
